import { Component } from '@angular/core';

import { AlertService } from '#utils/alert-service/alert-service.service';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { FuseAlertComponent } from '@fuse/components/alert';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { Observable, filter } from 'rxjs';
import { AppVersionService } from './shared/services/version';

/** Tiempo mínimo entre búsquedas de actualización al volver a la app. */
const UPDATE_CHECK_THROTTLE_MS = 5 * 60 * 1000;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet, CommonModule, FuseAlertComponent]
})
export class AppComponent {
    /**
     * Constructor
     */
    constructor(
        private alertService: AlertService,
        private matDialog: MatDialog,
        private _appVersionService: AppVersionService,
        private _swUpdate: SwUpdate,
        private _fuseConfirmationService: FuseConfirmationService,
        private _router: Router,
    ) {
        this.errorMessage$ = this.alertService.message$;
        this.updateMessage$ = this.alertService.updateMessage$;
        this.saveMessage$ = this.alertService.saveMessage$;
        this.changelog();
        this._listenForAppUpdates();
        this._listenForPushNavigation();
    }

    errorMessage$: Observable<{ message: string, errors: Array<string> | null }>;
    updateMessage$: Observable<string>;
    saveMessage$: Observable<string>;


    public async changelog() {
        /* if (await this._appVersionService.outdated()) {
            this.matDialog.open(VersionChangelogComponent, {
                width: '100%',
                maxWidth: '600px',
                maxHeight: '90vh',
                disableClose: true,
            })
        } */
    }

    /**
     * Al tocar una notificación push con el panel ya abierto, el service worker pide navegar a su ruta
     * (ver public/firebase-messaging-sw.js).
     */
    private _listenForPushNavigation(): void {
        navigator.serviceWorker?.addEventListener('message', (event: MessageEvent) => {
            if (event.data?.type === 'PUSH_NAVIGATE' && typeof event.data.url === 'string') {
                this._router.navigateByUrl(event.data.url);
            }
        });
    }

    /**
     * PWA: la app instalada en el celular no se actualiza sola después de un deploy.
     * Cuando el service worker ya descargó la versión nueva, se ofrece recargar.
     * Si el usuario elige "Más tarde", la versión nueva se usa la próxima vez que abra la app.
     */
    private _listenForAppUpdates(): void {
        if (!this._swUpdate.isEnabled) {
            return;
        }

        let promptOpen = false;
        this._swUpdate.versionUpdates
            .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
            .subscribe(() => {
                if (promptOpen) {
                    return;
                }
                promptOpen = true;
                this._fuseConfirmationService
                    .open({
                        title: 'Nueva versión disponible',
                        message: 'Hay una nueva versión del ERP. Si estás llenando un formulario, guárdalo antes de recargar.',
                        icon: { name: 'heroicons_outline:arrow-path', color: 'primary' },
                        actions: {
                            confirm: { label: 'Recargar', color: 'primary' },
                            cancel: { label: 'Más tarde' },
                        },
                    })
                    .afterClosed()
                    .subscribe((result) => {
                        promptOpen = false;
                        if (result === 'confirmed') {
                            document.location.reload();
                        }
                    });
            });

        // La versión en caché quedó inservible (p. ej. le faltan archivos tras un deploy)
        this._swUpdate.unrecoverable.subscribe(() => document.location.reload());

        // La app instalada suele quedar abierta en segundo plano días enteros:
        // al volver a primer plano se buscan actualizaciones (como máximo cada 5 min).
        let lastCheck = Date.now();
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible' || Date.now() - lastCheck < UPDATE_CHECK_THROTTLE_MS) {
                return;
            }
            lastCheck = Date.now();
            this._swUpdate.checkForUpdate().catch(() => undefined);
        });
    }
}
